import React from "react";
import PropTypes from "prop-types";
import { graphql, useStaticQuery } from "gatsby";

import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";

/*
  This component requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/

export const query = graphql`
  fragment BlogThreeColumnCardsArticle on StrapiArticle {
    id
    title
    locale
    url
    date
    categories {
      url
      title
    }
    author {
      url
      name
      image {
        ...StrapiMediaSubquery
      }
    }
    readingTimeMinutes
    description
    descriptionShort
    headImage {
      ...StrapiMediaSubquery
    }
  }

  fragment BlogThreeColumnCardsData on StrapiComponentBlogThreeColumnCards {
    topic
    title
    description
    readingTimeText
    showAuthors {
      articles {
        id
      }
    }
    showCategories {
      articles {
        id
      }
    }
  }
`;

const descriptionMaxWordCount = 50;

function limitTextByWordCount(text, wordMaxCount) {
  let wordSplits = text.split(" ");
  let wordCount = wordSplits?.length;
  if (wordCount > wordMaxCount) {
    wordSplits = wordSplits.slice(0, wordMaxCount);
    return wordSplits.join(" ") + "...";
  } else {
    return text;
  }
}

function ThreeColumnCards({ data, articles = [] }) {
  if (!data) {
    return <template />;
  }
  if (
    data.showAuthors?.length ||
    data?.showCategories?.length ||
    data?.showAllArticles
  ) {
    const query = useStaticQuery(
      graphql`
        query blogThreeColumnCards {
          articles: allStrapiArticle {
            nodes {
              id
              ...BlogThreeColumnCardsArticle
            }
          }
        }
      `
    );

    if (data?.showAllArticles) {
      articles = query?.articles?.nodes || [];
    } else {
      const articleIds = [];
      for (let author of data?.showAuthors || []) {
        for (let article of author.articles || []) {
          if (article.id && !articleIds.find((id) => id === article.id)) {
            articleIds.push(article.id);
          }
        }
      }
      for (let category of data?.showCategories || []) {
        for (let article of category.articles || []) {
          if (article.id && !articleIds.find((id) => id === article.id)) {
            articleIds.push(article.id);
          }
        }
      }
      articles = query?.articles?.nodes?.filter((article) =>
        articleIds.find((id) => id === article.id)
      );
    }
  }

  return (
    <div className="mx-auto max-w-7xl bg-background-white px-4 pb-20 pt-16 sm:px-6 lg:px-8 lg:pb-28 lg:pt-24">
      <div className="text-center">
        {data.topic && (
          <h3 className="block text-center text-base font-semibold uppercase tracking-wide text-text-primary">
            <MarkdownWrapper text={data.topic} />
          </h3>
        )}
        {data.title && (
          <h2 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl">
            <MarkdownWrapper text={data.title} />
          </h2>
        )}
        {data.description && (
          <MarkdownWrapper
            text={data.description}
            markdownClassName="mt-3 max-w-2xl mx-auto text-xl text-text-secondary sm:mt-4"
            replaceParagraph={false}
          />
        )}
      </div>
      <div className="mx-auto mt-12 grid max-w-lg gap-5 lg:max-w-none lg:grid-cols-3">
        {Boolean(articles?.length) &&
          articles.map((article, index) => (
            <div
              key={index}
              className="flex flex-col overflow-hidden rounded-lg bg-background-white shadow-lg hover:bg-background-light-gray"
            >
              {article.headImage && (
                <LinkWrapper to={article.url} className="flex-shrink-0">
                  <MediaWrapper
                    media={article.headImage}
                    divClassName="w-full aspect-[1/1]"
                    className="h-full w-full"
                  />
                </LinkWrapper>
              )}
              <div className="flex h-full flex-col justify-between bg-background-transparent p-4">
                <div className="flex flex-shrink-0 flex-col">
                  {Boolean(article.categories?.length) &&
                    article.categories[0]?.title && (
                      <div className="flex">
                        <LinkWrapper
                          to={article.categories[0].url}
                          className="rounded-md px-2 hover:underline"
                        >
                          <div className="block">
                            <MarkdownWrapper
                              text={article.categories[0].title}
                              markdownClassName="text-sm font-medium text-text-secondary"
                            />
                          </div>
                        </LinkWrapper>
                      </div>
                    )}
                  {(article.title ||
                    article.descriptionShort ||
                    article.description) && (
                    <LinkWrapper
                      to={article.url}
                      className="mt-2 rounded-md px-2 hover:underline"
                    >
                      {article.title && (
                        <MarkdownWrapper
                          text={article.title}
                          markdownClassName="custom-font text-xl font-semibold text-text-primary"
                        />
                      )}
                      {(article.descriptionShort || article.description) && (
                        <MarkdownWrapper
                          text={
                            article.descriptionShort ||
                            limitTextByWordCount(
                              article.description,
                              descriptionMaxWordCount
                            )
                          }
                          markdownClassName="mt-3 text-base text-text-secondary"
                          replaceParagraph={false}
                        />
                      )}
                    </LinkWrapper>
                  )}
                </div>
                <div className="mt-6 flex items-center">
                  {article.author?.name && (
                    <div className="flex-shrink-0">
                      <LinkWrapper to={article.author?.url}>
                        <span className="sr-only">{article.author.name}</span>
                        {article.author.image && (
                          <MediaWrapper
                            media={article.author.image}
                            divClassName="h-10 w-10"
                            className="rounded-full"
                          />
                        )}
                      </LinkWrapper>
                    </div>
                  )}
                  <div className="ml-3">
                    {article.author?.name && (
                      <div className="flex">
                        <LinkWrapper
                          to={article.author?.url}
                          className="-ml-2 rounded-md px-2 hover:underline"
                        >
                          <MarkdownWrapper
                            text={article.author.name}
                            markdownClassName="text-sm font-medium text-text-primary"
                          />
                        </LinkWrapper>
                      </div>
                    )}
                    <div className="flex space-x-1 text-sm text-text-secondary">
                      {article.date && article.locale && (
                        <time
                          dateTime={new Date(article.date).toLocaleDateString(
                            article.locale,
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }
                          )}
                        >
                          {new Date(article.date).toLocaleDateString(
                            article.locale,
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }
                          )}
                        </time>
                      )}
                      {data.readingTimeText && article.readingTimeMinutes && (
                        <span>
                          {article.date && article.locale && (
                            <span aria-hidden="true" className="px-1">
                              &middot;
                            </span>
                          )}
                          <span>{`${article.readingTimeMinutes} ${data.readingTimeText}`}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

ThreeColumnCards.propTypes = {
  data: PropTypes.object,
  articles: PropTypes.array,
};

export default ThreeColumnCards;
